import { useCallback, useEffect, useRef } from 'react';
import { isExtensionAutoAuthEnabled } from '../config/extensionTokensFlags';
import { getMyExtensionBootstrap } from '../services/featureFlags.api';
import { getExtensionId, sendConfigToExtension } from '../services/extensionHandshake';
import { sanitizeExtensionCodingProfiles } from '../utils/codingProfiles';

/** Payload shape version. Bump only when the shape changes, never for new keys. */
export const EXTENSION_CONFIG_VERSION = 1;

/** How long a fetched feature set is considered fresh (AD-FP-1). */
export const CONFIG_TTL_MS = 5 * 60 * 1000;

/**
 * Rejections the extension will never accept on a retry. Retrying these on every
 * focus forever would be a busy loop that hides a real version mismatch.
 */
const PERMANENT_PUSH_REASONS = Object.freeze([
  'unsupported_version',
  'invalid_config',
  'invalid_features',
  'unknown_type',
]);

/**
 * Wrap the backend's effective features in the versioned envelope the extension
 * stores. Feature keys are carried through verbatim — renaming them here would
 * make the website a second naming authority over the source of truth — and only
 * true keys are present, so the extension's "absent means disabled" rule is the
 * backend's fail-closed semantics rather than a second implementation of them.
 *
 * `issuedAt` is the time the features were *fetched*, not pushed, so the
 * extension can order two tabs' payloads correctly when a stale one is retried.
 *
 * HS-8.4.1: optional `codingProfiles` is additive (AD-12). Always an array when
 * provided so the extension can clear stale handles after unlink.
 *
 * @param {Record<string, boolean>} features
 * @param {number} [fetchedAt] epoch ms the features were fetched
 * @param {unknown} [codingProfiles]
 * @returns {{ version: number, issuedAt: string, features: Record<string, boolean>, codingProfiles: { type: string, username: string, profileUrl?: string }[] }}
 */
export function buildExtensionConfig(features, fetchedAt, codingProfiles) {
  const resolved = {};
  for (const [key, value] of Object.entries(features || {})) {
    if (value === true) resolved[key] = true;
  }
  return {
    version: EXTENSION_CONFIG_VERSION,
    issuedAt: new Date(Number.isFinite(fetchedAt) ? fetchedAt : Date.now()).toISOString(),
    features: resolved,
    codingProfiles: sanitizeExtensionCodingProfiles(codingProfiles),
  };
}

/**
 * Keep the extension's feature configuration in step with the backend.
 *
 * Deliberately independent of token provisioning (AD-FP-1): a healthy extension
 * never triggers a session call, so configuration delivered on that path would
 * never refresh and a disabled feature could never switch itself off.
 *
 * @param {{ enabled: boolean }} params
 * @returns {{ provisionNow: () => Promise<void> }}
 */
export default function useExtensionConfigProvisioning({ enabled }) {
  const featuresRef = useRef(null);
  const codingProfilesRef = useRef([]);
  const fetchedAtRef = useRef(0);
  const pushPendingRef = useRef(true);
  const inFlightRef = useRef(false);
  const queuedForceRef = useRef(false);
  /**
   * Incremented whenever the identity behind the cache changes (sign-out). An
   * in-flight fetch from a previous generation must not write its result back —
   * otherwise student A's grants land in the cache and the next student to sign
   * in reads them as fresh and never re-fetches.
   */
  const generationRef = useRef(0);

  const provision = useCallback(async ({ force = false } = {}) => {
    if (!isExtensionAutoAuthEnabled() || !getExtensionId()) return;

    // A forced call (fresh handshake) must not be dropped just because the
    // periodic one is mid-flight — that is the one moment it matters most.
    if (inFlightRef.current) {
      if (force) queuedForceRef.current = true;
      return;
    }

    const fresh = Date.now() - fetchedAtRef.current < CONFIG_TTL_MS && featuresRef.current;
    // A fresh, already-delivered config needs no work at all — that is what keeps
    // an idle dashboard from polling.
    if (fresh && !force && !pushPendingRef.current) return;

    inFlightRef.current = true;
    const generation = generationRef.current;
    try {
      if (!fresh || force) {
        const bootstrap = await getMyExtensionBootstrap();
        // Sign-out (or an account change) happened while this was in flight.
        if (generation !== generationRef.current) return;
        featuresRef.current = bootstrap.features;
        // Omit ⇒ keep last trusted handles (API may omit on User read failure).
        // Explicit [] ⇒ unlink — clear ExtensionIdentity on next push.
        if (Array.isArray(bootstrap.codingProfiles)) {
          codingProfilesRef.current = bootstrap.codingProfiles;
        }
        fetchedAtRef.current = Date.now();
      }
      const result = await sendConfigToExtension(
        buildExtensionConfig(
          featuresRef.current,
          fetchedAtRef.current,
          codingProfilesRef.current
        )
      );
      if (generation !== generationRef.current) return;

      if (result?.ok) {
        pushPendingRef.current = false;
      } else if (PERMANENT_PUSH_REASONS.includes(result?.reason)) {
        // Retrying will not help; surface it loudly instead of looping quietly.
        pushPendingRef.current = false;
        console.warn(
          `[stacktrack] extension rejected the feature configuration (${result.reason}) — `
            + 'the installed extension may be older than this site build'
        );
      } else {
        // Extension asleep, not installed, or mid-update: retry next cycle.
        pushPendingRef.current = true;
      }
    } catch (err) {
      pushPendingRef.current = true;
      console.debug('[stacktrack] feature provisioning skipped', err?.message || err);
    } finally {
      inFlightRef.current = false;
      if (queuedForceRef.current && generation === generationRef.current) {
        queuedForceRef.current = false;
        void provision({ force: true });
      }
    }
  }, []);

  const provisionNow = useCallback(() => provision({ force: true }), [provision]);

  useEffect(() => {
    if (!enabled) {
      // Sign-out: invalidate any in-flight fetch and drop the cache so the next
      // sign-in cannot inherit another account's feature set.
      generationRef.current += 1;
      featuresRef.current = null;
      codingProfilesRef.current = [];
      fetchedAtRef.current = 0;
      pushPendingRef.current = true;
      queuedForceRef.current = false;
      return undefined;
    }

    void provision();

    // Forced, because the tick lands exactly one TTL after the *previous fetch
    // started* — the freshness check would otherwise see `TTL - latency`, judge
    // the cache fresh, and skip every other tick.
    const interval = setInterval(() => void provision({ force: true }), CONFIG_TTL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void provision();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, provision]);

  return { provisionNow };
}
